import axios from "axios";
import authService from "./authService";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://delivery-bonfimgourmet-244425130782.southamerica-east1.run.app";

const API_BASE = `${BACKEND_URL}/api/users`;

/**
 * User Service
 * Gerencia todas as operações CRUD de usuários
 */
class UserService {
  /**
   * Get authorization headers with JWT token
   * @returns {object} Headers object
   */
  getAuthHeaders() {
    const token = authService.getToken();
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  }

  /**
   * Get all users
   * @returns {Promise<Array>}
   */
  async getAllUsers() {
    try {
      const response = await axios.get(API_BASE, this.getAuthHeaders());
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error("❌ Erro ao buscar usuários:", error);
      throw error;
    }
  }

  /**
   * Get user by ID
   * @param {number} id - User ID
   * @returns {Promise<object>}
   */
  async getUserById(id) {
    try {
      const response = await axios.get(
        `${API_BASE}/${id}`,
        this.getAuthHeaders()
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`❌ Erro ao buscar usuário ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get user by email
   * @param {string} email - User email
   * @returns {Promise<object>}
   */
  async getUserByEmail(email) {
    try {
      const response = await axios.get(
        `${API_BASE}/email/${email}`,
        this.getAuthHeaders()
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`❌ Erro ao buscar usuário por email ${email}:`, error);
      throw error;
    }
  }

  /**
   * Create new user
   * @param {object} userData - User data
   * @returns {Promise<object>}
   */
  async createUser(userData) {
    try {
      const response = await axios.post(
        API_BASE,
        userData,
        this.getAuthHeaders()
      );
      console.log("✅ Usuário criado com sucesso");
      return response.data?.data || response.data;
    } catch (error) {
      console.error("❌ Erro ao criar usuário:", error);
      throw error;
    }
  }

  /**
   * Update user
   * @param {number} id - User ID
   * @param {object} userData - User data
   * @returns {Promise<object>}
   */
  async updateUser(id, userData) {
    try {
      const response = await axios.put(
        `${API_BASE}/${id}`,
        userData,
        this.getAuthHeaders()
      );
      console.log(`✅ Usuário ${id} atualizado com sucesso`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`❌ Erro ao atualizar usuário ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete user
   * @param {number} id - User ID
   * @returns {Promise<void>}
   */
  async deleteUser(id) {
    try {
      await axios.delete(`${API_BASE}/${id}`, this.getAuthHeaders());
      console.log(`✅ Usuário ${id} deletado com sucesso`);
    } catch (error) {
      console.error(`❌ Erro ao deletar usuário ${id}:`, error);
      throw error;
    }
  }

  /**
   * Validate email format
   * @param {string} email
   * @returns {boolean}
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate password strength
   * @param {string} password
   * @returns {object} {valid: boolean, message: string}
   */
  validatePassword(password) {
    if (!password || password.length < 6) {
      return {
        valid: false,
        message: "Senha deve ter no mínimo 6 caracteres",
      };
    }
    return { valid: true, message: "" };
  }

  /**
   * Validate user data before submit
   * @param {object} userData
   * @returns {object} {valid: boolean, errors: object}
   */
  validateUserData(userData, isEditing = false) {
    const errors = {};

    // Email validation
    if (!userData.email || !userData.email.trim()) {
      errors.email = "Email é obrigatório";
    } else if (!this.isValidEmail(userData.email)) {
      errors.email = "Email inválido";
    }

    // Password validation (only for new users or if password is provided)
    if (!isEditing || userData.password) {
      const passwordValidation = this.validatePassword(userData.password);
      if (!passwordValidation.valid) {
        errors.password = passwordValidation.message;
      }
    }

    // Role validation
    if (!userData.role || !userData.role.trim()) {
      errors.role = "Função é obrigatória";
    }

    return {
      valid: Object.keys(errors).length === 0,
      errors,
    };
  }
}

// Export singleton instance
const userService = new UserService();
export default userService;
